import { useState } from 'react';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  Link
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { login } from '../../api/modules/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { showMessage } from '../../utils';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)', 
  borderRadius: 16,
  margin: '20px',
  [theme.breakpoints.up('xs')]: {
    width: `calc(100% - 100px)`,
    maxWidth: 320,
  },
  [theme.breakpoints.up('md')]: {
    width: '100%',
    maxWidth: 480
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3)
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#007AFF',
  padding: '12px',
  borderRadius: '24px',
  fontSize: '16px',
  fontWeight: 700,
  height: 48,
  boxShadow: '0 2px 8px rgba(0,51,161,0.08)',
  '&:hover': {
    backgroundColor: '#005BB5',
  },
}));

function setSession(firmServiceToken) {
  if (firmServiceToken) {
    localStorage.setItem('firmServiceToken', firmServiceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${firmServiceToken}`;
  } else {
    localStorage.removeItem('firmServiceToken');
    localStorage.removeItem('firm_login_account');
    localStorage.removeItem('firmRefreshToken');
    localStorage.removeItem('device_id');
    // localStorage.clear();
    delete axios.defaults.headers.common.Authorization;
  }
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false
  });

  // 添加错误状态
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });

  // 获取URL中的inviteCode
  const searchParams = new URLSearchParams(window.location.search);
  const inviteCode = searchParams.get('inviteCode');

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'remember' ? checked : value
    }));
    
    // 实时清除错误提示
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 添加表单验证
  const validateForm = () => {
    let tempErrors = {};
    let isValid = true;

    // 邮箱验证
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!formData.email) {
      tempErrors.email = 'Email is required';
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      tempErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // 密码验证
    if (!formData.password) {
      tempErrors.password = 'Password is required';
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  // 修正递归获取最深层 from 的方法
  const getDeepestFrom = (obj) => {
    let current = obj;
    console.log('current', current);
    // 递归到底层，直到没有更深的state.from
    while (current && current.state && current.state.from && current.state.from !== current) {
      current = current.state.from;
      console.log('current11', current);
    }
    return current;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (validateForm()) {
        try {
            const registerData = {
                email: formData.email,
                password: formData.password
            };
            await login(registerData, (rst) => {
                let { code, data, msg } = rst;
                if (code === 200) {
                    showMessage(msg, 'success');
                    setTimeout(() => {
                        setSession(data.token);
                        
                        // 获取 URL 查询参数
                        const searchParams = new URLSearchParams(window.location.search);
                        const isInvitedUser = searchParams.has('v') && searchParams.get('v') === 'isInvitedUser';
                        const isAgentUser = searchParams.has('v') && searchParams.get('v') === 'isAgentUser';

                        console.log('isInvitedUser:', isInvitedUser); // 添加日志
                        console.log('isAgentUser:', isAgentUser); // 添加日志

                        if (isInvitedUser) {
                            console.log('Navigating to tax declaration for invited user');
                            navigate('/campaigns/invitations/tax-declaration');// 被邀请用户跳转
                        } else if (isAgentUser) {
                            console.log('Navigating to invitations for agent user');
                            navigate('/'); // 代理人跳转
                        } else {
                            console.log('Navigating to default path');
                            navigate('/'); // 默认跳转
                        }
                    }, 0); // 设置为 0 毫秒
                } else {
                    showMessage(msg, 'error');
                }
            });
        } catch (error) {
            console.error('Login failed:', error);
        }
    }
  };

  // 修改注册按钮的跳转逻辑
  const handleSignUpClick = () => {
    if (inviteCode) {
      navigate(`/campaigns/invitations/signup?inviteCode=${inviteCode}`);
    } else {
      navigate('/campaigns/invitations/signup');
    }
  };

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#F7F9FC">
      {/* 左侧品牌区 */}
      <Box flex={1} display={{ xs: 'none', md: 'flex' }} flexDirection="column" alignItems="center" justifyContent="center">
        <Box component="img" src={`${process.env.PUBLIC_URL}/images/hello-door.png`} alt="Welcome" sx={{ width: 306, height: 241, mb: 4 }} />
        <Typography variant="h4" sx={{ color: '#031B4E', fontWeight: 700, fontSize: 36, mt: 2, textAlign: 'center' }}>
          Welcome Back!
        </Typography>
      </Box>

      {/* 右侧表单区 */}
      <Box flex={1} bgcolor="#fff" display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" px={3}>
        <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
          <Box
            component="img"
            src={require('../../assets/icons/utaxes.svg').default} 
            alt="uTaxes"
            sx={{
              width: 180,
              height: 'auto',
              display: { xs: 'block', md: 'none' },
              mx: 'auto',
              mb: 5
            }}
          />
          <Typography variant="h4" sx={{ color: '#031B4E', fontWeight: 700, mb: 1, textAlign: 'center' }}>
            Log in
          </Typography>
          <Typography sx={{ color: '#444', fontSize: 15, textAlign: 'center', mb: 3 }}>
            Still not part of uTaxes?{' '}
            <Link onClick={handleSignUpClick} sx={{ color: '#007AFF', fontWeight: 600, cursor: 'pointer' }}>Sign up</Link>
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8FAFC' } }}
              />
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                error={Boolean(errors.password)}
                helperText={errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8FAFC' } }}
              />
              <StyledButton
                fullWidth
                type="submit"
                variant="contained"
                size="large"
              >
                Log in
              </StyledButton>
            </Stack>
          </form>
        </Box>
      </Box>
    </Box>
  );
}